import React, { useCallback, useEffect, useState, ReactNode } from "react";
import HeaderAdmin from "./HeaderAdmin";
import SideNavAdmin from "./SideNavAdmin";
import FooterAdmin from "./FooterAdmin";

type HeadProps = { title: string };
type LayoutProps = { children?: ReactNode; title?: string };

const Head: React.FC<HeadProps> = ({ title }) => {
  useEffect(() => {
    document.title = title;
  }, [title]);

  return null;
};

const COLLAPSE_KEY = "hc_sidebar_collapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
};

const AdminLayout = ({ children, title = "" }: LayoutProps) => {
  // tablet/desktop: icon-only sidebar (remembered); phones: slide-in drawer
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? "0" : "1");
      } catch {
        /* storage unavailable: just don't remember */
      }
      return !c;
    });
  }, []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  // Escape closes the phone drawer
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  return (
    <main className="min-h-screen bg-hcBg">
      <Head title={title} />
      <HeaderAdmin
        sidebarCollapsed={collapsed}
        menuOpen={mobileOpen}
        onMenuClick={() => setMobileOpen((o) => !o)}
      />

      <SideNavAdmin
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        mobileOpen={mobileOpen}
        onCloseMobile={closeMobile}
      />

      <div
        className={`flex min-h-screen flex-col pt-20 transition-[padding] duration-200 ${
          collapsed ? "md:pl-[76px]" : "md:pl-64"
        }`}
      >
        <div className="min-w-0 flex-1 px-4 py-6 sm:px-7 sm:py-8">{children}</div>
        <FooterAdmin />
      </div>

    </main>
  );
};

export default AdminLayout;
